import { useCallback, useMemo, useState } from "react";
import type { PortalWidget } from "@entrilia/shared/api/portalWidgetsTypes";
import useFetch from "@entrilia/shared/hooks/useFetch";
import type { DescriptiveDateRange } from "@entrilia/shared/utilities/dateFilterHelper";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import { api } from "../../api/client";
import type { CashFlowPaymentStatus, ReportRow } from "../../api/types/widgetTypes";
import { searchTabularReportRows } from "../common/reports/tabularReportFiltering";

interface Options {
  rowsLimit?: number;
}

const useFilteredRows = (rows: ReportRow[] | undefined, searchValue: string) =>
  useMemo(() => (rows === undefined ? [] : searchTabularReportRows(rows, searchValue)), [rows, searchValue]);

export const useBalanceWidgetData = (widgetSettings: PortalWidget, { rowsLimit }: Options = {}) => {
  const [reportingDate, setReportingDate] = useState<string>();
  const [investorIds, setInvestorIds] = useState<string[]>([]);
  const [fundIds, setFundIds] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState("");

  const [parameters, fetchParametersError] = useFetch(api.widgets.getBalanceReportParameters, {
    onSetData: (data) => {
      const firstReportingDate = data.reportingDates[0];
      if (firstReportingDate) {
        setReportingDate(firstReportingDate);
      }
    },
  });

  const noDataText = useMemo(() => {
    if (parameters && parameters.reportingDates.length === 0) {
      return "No reporting dates available";
    }
    if (parameters && !widgetSettings.reportId) {
      return "Report is not configured";
    }
    return undefined;
  }, [parameters, widgetSettings.reportId]);

  const isRunReportDisabled = !reportingDate || !widgetSettings.reportId;
  const runReport = useCallback(
    (signal?: AbortSignal) =>
      api.widgets.runBalanceReport(
        {
          reportId: defined(widgetSettings.reportId),
          reportingDate: defined(reportingDate),
          investorIds,
          fundIds,
          rowsLimit,
        },
        signal
      ),
    [fundIds, investorIds, reportingDate, rowsLimit, widgetSettings.reportId]
  );

  const [report, runReportError, { isFetching }] = useFetch(runReport, {
    disabled: isRunReportDisabled,
  });

  const filteredRows = useFilteredRows(report?.rows, searchValue);

  const clearFilters = useCallback(() => {
    setInvestorIds([]);
    setFundIds([]);
  }, []);

  const isAnyFilterApplied = investorIds.length > 0 || fundIds.length > 0;

  return {
    parameters,
    report,
    fetchError: fetchParametersError ?? runReportError,
    isRunningReport: isFetching,
    noDataText,
    reportingDate,
    setReportingDate,
    investorIds,
    setInvestorIds,
    fundIds,
    setFundIds,
    searchValue,
    setSearchValue,
    filteredRows,
    clearFilters,
    isAnyFilterApplied,
  };
};

export const usePortfolioWidgetData = (widgetSettings: PortalWidget, { rowsLimit }: Options = {}) => {
  const [fundId, setFundId] = useState<string>();
  const [searchValue, setSearchValue] = useState("");

  const [parameters, fetchParametersError] = useFetch(api.widgets.getPortfolioReportParameters, {
    onSetData: (data) => {
      const firstFundId = data.funds[0]?.id;
      if (firstFundId) {
        setFundId(firstFundId);
      }
    },
  });

  const noDataText = useMemo(() => {
    if (parameters && parameters.funds.length === 0) {
      return "No investment funds available";
    }
    if (parameters && !widgetSettings.reportId) {
      return "Report is not configured";
    }
    return undefined;
  }, [parameters, widgetSettings.reportId]);

  const isRunReportDisabled = !fundId || !widgetSettings.reportId;
  const runReport = useCallback(
    (signal?: AbortSignal) =>
      api.widgets.runPortfolioReport(
        { reportId: defined(widgetSettings.reportId), fundId: defined(fundId), rowsLimit },
        signal
      ),
    [fundId, rowsLimit, widgetSettings.reportId]
  );

  const [report, runReportError, { isFetching }] = useFetch(runReport, {
    disabled: isRunReportDisabled,
  });

  const filteredRows = useFilteredRows(report?.rows, searchValue);

  return {
    parameters,
    report,
    fetchError: fetchParametersError ?? runReportError,
    isRunningReport: isFetching,
    noDataText,
    fundId,
    setFundId,
    searchValue,
    setSearchValue,
    filteredRows,
  };
};

export const useCashFlowsWidgetData = (widgetSettings: PortalWidget, { rowsLimit }: Options = {}) => {
  const [investorIds, setInvestorIds] = useState<string[]>([]);
  const [fundIds, setFundIds] = useState<string[]>([]);
  const [paymentStatuses, setPaymentStatuses] = useState<CashFlowPaymentStatus[]>([]);
  const [dateRange, setDateRange] = useState<DescriptiveDateRange>();
  const [searchValue, setSearchValue] = useState("");

  const [parameters, fetchParametersError] = useFetch(api.widgets.getCashFlowsReportParameters);

  const runReport = useCallback(
    (signal?: AbortSignal) =>
      api.widgets.runCashFlowsReport(
        {
          investorIds,
          fundIds,
          paymentStatuses,
          fromDueDate: dateRange?.from?.toISOString(),
          toDueDate: dateRange?.to?.toISOString(),
          rowsLimit,
          includeRowDetails: widgetSettings.includeRowDetails,
        },
        signal
      ),
    [dateRange?.from, dateRange?.to, fundIds, investorIds, paymentStatuses, rowsLimit, widgetSettings.includeRowDetails]
  );

  const [report, runReportError, { isFetching }] = useFetch(runReport);

  const filteredRows = useFilteredRows(report?.rows, searchValue);

  const clearFilters = useCallback(() => {
    setInvestorIds([]);
    setFundIds([]);
    setPaymentStatuses([]);
    setDateRange(undefined);
  }, []);

  const isAnyFilterApplied =
    investorIds.length > 0 || fundIds.length > 0 || paymentStatuses.length > 0 || dateRange !== undefined;

  return {
    parameters,
    report,
    fetchError: fetchParametersError ?? runReportError,
    isRunningReport: isFetching,
    investorIds,
    setInvestorIds,
    fundIds,
    setFundIds,
    paymentStatuses,
    setPaymentStatuses,
    dateRange,
    setDateRange,
    searchValue,
    setSearchValue,
    filteredRows,
    clearFilters,
    isAnyFilterApplied,
  };
};
